import { useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { projectExportConfigSchema } from "@iamu/shared";
import { trpc } from "@/lib/trpc";
import { useSetBreadcrumb } from "@/components/layout/breadcrumb-context";
import { isValidUuid } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/freshness";
import {
  type FindingType,
  type Severity,
  type CanonicalState,
  normalizeCanonicalStatus,
} from "@/components/finding-badges";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import { useTransitionFindings } from "@/components/experience/use-transition-findings";
import {
  resolveFindingTitle,
  resolveFindingDescription,
  resolveFindingType,
  resolveFindingSeverity,
  resolveFindingState,
} from "@/lib/finding-resolution";
import type { TargetBox } from "@/components/walkthrough/step-screenshot";
import type {
  FindingDetailEvidence,
  FindingCitation,
  FindingObservationItem,
} from "./finding-detail-types";

export function useFindingDetail() {
  const { id: projectId, findingId } = useParams<{
    id: string;
    findingId: string;
  }>();
  const location = useLocation();
  const fromState = location.state as
    | { from?: string; label?: string }
    | undefined;

  const isValidId = isValidUuid(findingId);

  const { data, isLoading, error, refetch } = trpc.finding.getById.useQuery(
    { id: findingId! },
    { enabled: isValidId },
  );

  const isCanonical = data?.isCanonical === true;

  const dataState = isCanonical
    ? ((data as { state?: string }).state ?? null)
    : null;

  const back = useMemo(() => {
    if (fromState?.from) {
      return {
        to: fromState.from,
        label: fromState.label ?? "Back",
      };
    }
    return {
      to:
        dataState === "new"
          ? `/projects/${projectId}/findings?view=triage`
          : `/projects/${projectId}/findings`,
      label: "Findings",
    };
  }, [fromState, dataState, projectId]);

  const { data: projectContext } = trpc.project.getContext.useQuery(
    { projectId: projectId! },
    { enabled: !!projectId },
  );
  const exportConfig = projectContext?.exportConfig
    ? projectExportConfigSchema.parse(projectContext.exportConfig)
    : undefined;

  const { data: workflow } = trpc.workflow.get.useQuery(
    { canonicalId: findingId! },
    { enabled: !!findingId && isCanonical },
  );

  const { data: gateHistory } = trpc.finding.gateHistory.useQuery(
    {
      projectId: projectId!,
      canonicalId: findingId!,
      limit: 20,
    },
    { enabled: !!projectId && !!findingId && isCanonical },
  );

  const requests = useWorkRequests(projectId ?? "");
  const transition = useTransitionFindings(projectId ?? "");

  const title = resolveFindingTitle(data as any, "Untitled Finding");
  const description = resolveFindingDescription(data as any);

  useSetBreadcrumb(data ? title : null);

  const openRequest =
    isCanonical && data
      ? (requests.byCanonical.get(data.id as string) ?? null)
      : null;

  const uiFact = isCanonical
    ? ((data as { effectiveUiFact?: string | null })?.effectiveUiFact ?? null)
    : ((data as { uiFact?: string | null })?.uiFact ?? null);

  const consequence = isCanonical
    ? ((data as { effectiveConsequence?: string | null })
        ?.effectiveConsequence ?? null)
    : ((data as { consequence?: string | null })?.consequence ?? null);

  const what = isCanonical
    ? ((data as { effectiveWhat?: string | null })?.effectiveWhat ?? null)
    : ((data as { what?: string | null })?.what ?? null);

  const why = isCanonical
    ? ((data as { effectiveWhy?: string | null })?.effectiveWhy ?? null)
    : ((data as { why?: string | null })?.why ?? null);

  const basis = isCanonical
    ? ((data as { effectiveBasis?: string | null })?.effectiveBasis ?? null)
    : ((data as { basis?: string | null })?.basis ?? null);

  const screen = isCanonical
    ? ((data as { effectiveScreen?: string | null })?.effectiveScreen ?? null)
    : ((data as { screen?: string | null })?.screen ?? null);

  const breaksRule = isCanonical
    ? ((data as { effectiveBreaksRule?: string | null })?.effectiveBreaksRule ??
      null)
    : ((data as { breaksRule?: string | null })?.breaksRule ?? null);

  const breaksFile = isCanonical
    ? ((data as { effectiveBreaksFile?: string | null })?.effectiveBreaksFile ??
      null)
    : ((data as { breaksFile?: string | null })?.breaksFile ?? null);

  const scenarioName =
    (
      data as {
        latestScenarioName?: string | null;
        scenarioName?: string | null;
      }
    )?.latestScenarioName ??
    (data as { scenarioName?: string | null })?.scenarioName;

  const scenarioId =
    (data as { latestScenarioId?: string | null; scenarioId?: string | null })
      ?.latestScenarioId ??
    (data as { scenarioId?: string | null })?.scenarioId;

  const personaName = (data as { personaName?: string | null })?.personaName;
  const personaRole = (data as { personaRole?: string | null })?.personaRole;
  const personaIdForLink = (data as { personaId?: string | null })?.personaId;

  const severity = resolveFindingSeverity(data as any, "nit") as Severity;
  const type = resolveFindingType(data as any, "hinders") as FindingType;

  const rawRecommendation = isCanonical
    ? ((data as { effectiveRecommendation?: string | null })
        ?.effectiveRecommendation ?? null)
    : ((data as { recommendation?: string | null })?.recommendation ?? null);

  const recommendation = useMemo(() => {
    if (!rawRecommendation) return null;
    const stripped = rawRecommendation.replace(/<!--[\s\S]*?-->/g, "").trim();
    return stripped.length > 0 ? stripped : null;
  }, [rawRecommendation]);

  const state: CanonicalState = resolveFindingState(data as any);
  const stateReason = isCanonical
    ? ((data as { stateReason?: string | null })?.stateReason ?? null)
    : null;

  const status = normalizeCanonicalStatus(
    state ??
      ((data as { status?: string })?.status as string | undefined) ??
      "open",
  );

  const triageNoteFromData =
    typeof (data as { triageNote?: unknown })?.triageNote === "string"
      ? ((data as { triageNote: string }).triageNote as string)
      : "";

  const observationCount = isCanonical
    ? typeof (data as { observationCount?: unknown })?.observationCount ===
      "number"
      ? ((data as { observationCount: number }).observationCount as number)
      : 1
    : 1;

  const observations = useMemo((): FindingObservationItem[] => {
    if (!isCanonical) return [];
    const raw = (data as { observations?: unknown })?.observations;
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (obs: unknown): obs is FindingObservationItem =>
        typeof obs === "object" &&
        obs !== null &&
        "id" in obs &&
        typeof (obs as { id: unknown }).id === "string" &&
        "walkthroughId" in obs &&
        typeof (obs as { walkthroughId: unknown }).walkthroughId === "string" &&
        "createdAt" in obs &&
        typeof (obs as { createdAt: unknown }).createdAt === "string",
    );
  }, [isCanonical, data]);

  const evidence = useMemo((): FindingDetailEvidence | null => {
    if (!data) return null;
    if (isCanonical) {
      const canonicalData = data as {
        effectiveTargetBox?: TargetBox | null;
        effectiveStepNumber?: number | null;
        effectiveWalkthroughId?: string | null;
      };
      if (
        canonicalData.effectiveWalkthroughId &&
        typeof canonicalData.effectiveStepNumber === "number"
      ) {
        const matchingObs = observations.find(
          (o) =>
            o.walkthroughId === canonicalData.effectiveWalkthroughId &&
            o.stepNumber === canonicalData.effectiveStepNumber,
        );
        return {
          walkthroughId: canonicalData.effectiveWalkthroughId,
          stepNumber: canonicalData.effectiveStepNumber,
          stepTitle: matchingObs?.stepTitle ?? null,
          personaReasoning: matchingObs?.personaReasoning ?? null,
          surface: matchingObs?.surface ?? null,
          pageUrl: matchingObs?.pageUrl ?? null,
          targetBox: canonicalData.effectiveTargetBox ?? null,
        };
      }
      const obs = observations.find((o) => typeof o.stepNumber === "number");
      return obs
        ? {
            walkthroughId: obs.walkthroughId,
            stepNumber: obs.stepNumber as number,
            stepTitle: obs.stepTitle ?? null,
            personaReasoning: obs.personaReasoning ?? null,
            surface: obs.surface ?? null,
            pageUrl: obs.pageUrl ?? null,
            targetBox:
              (obs as { targetBox?: TargetBox | null }).targetBox ?? null,
          }
        : null;
    }
    const single = data as {
      walkthroughId?: string;
      stepNumber?: number | null;
      stepTitle?: string | null;
      personaReasoning?: string | null;
      stepSurface?: string | null;
      stepPageUrl?: string | null;
      targetBox?: TargetBox | null;
    };
    return single.walkthroughId && typeof single.stepNumber === "number"
      ? {
          walkthroughId: single.walkthroughId,
          stepNumber: single.stepNumber,
          stepTitle: single.stepTitle ?? null,
          personaReasoning: single.personaReasoning ?? null,
          surface: single.stepSurface ?? null,
          pageUrl: single.stepPageUrl ?? null,
          targetBox: single.targetBox ?? null,
        }
      : null;
  }, [data, isCanonical, observations]);

  const citations = useMemo((): FindingCitation[] => {
    const raw = (data as { citations?: unknown })?.citations;
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (cit: unknown): cit is FindingCitation =>
        typeof cit === "object" &&
        cit !== null &&
        "sourceType" in cit &&
        typeof (cit as { sourceType: unknown }).sourceType === "string" &&
        "sourceId" in cit &&
        typeof (cit as { sourceId: unknown }).sourceId === "string",
    );
  }, [data]);

  const fixStatus = !isCanonical
    ? ((data as { fixStatus?: string })?.fixStatus ?? "open")
    : null;

  const fixAttempts = useMemo(() => {
    if (isCanonical) return [];
    const raw = (data as { fixAttempts?: unknown })?.fixAttempts;
    return Array.isArray(raw) ? raw : [];
  }, [isCanonical, data]);

  const verificationResults = useMemo(() => {
    if (isCanonical) return [];
    const raw = (data as { verificationResults?: unknown })
      ?.verificationResults;
    return Array.isArray(raw) ? raw : [];
  }, [isCanonical, data]);

  const hasLinkedRepo = !isCanonical
    ? Boolean((data as { hasLinkedRepo?: boolean })?.hasLinkedRepo)
    : false;

  const canonicalParent = !isCanonical
    ? ((data as { canonicalParent?: { id?: string } | null })
        ?.canonicalParent ?? null)
    : null;

  const firstObservation =
    isCanonical && observations.length > 0
      ? observations.reduce((earliest, o) =>
          new Date(o.createdAt).getTime() <
          new Date(earliest.createdAt).getTime()
            ? o
            : earliest,
        )
      : null;

  const firstSeen =
    firstObservation?.createdAt ??
    (data as { createdAt?: string })?.createdAt ??
    (data as { firstSeenAt?: string })?.firstSeenAt;

  const firstSeenFormatted = firstSeen ? formatRelativeTime(firstSeen) : null;

  const priorityData =
    isCanonical &&
    data &&
    "priorityScore" in data &&
    typeof (data as { priorityScore: unknown }).priorityScore === "number"
      ? {
          priorityScore: (data as { priorityScore: number }).priorityScore,
          priorityOverride: ("priorityOverride" in data
            ? (data as { priorityOverride: string | null }).priorityOverride
            : null) as string | null,
        }
      : null;

  return {
    projectId: projectId!,
    findingId: findingId!,
    isValidId,
    data,
    isLoading,
    error,
    refetch,
    isCanonical,
    back,
    exportConfig,
    workflow,
    gateHistory,
    requests,
    transition,
    title,
    description,
    uiFact,
    consequence,
    what,
    why,
    basis,
    screen,
    breaksRule,
    breaksFile,
    scenarioName,
    scenarioId,
    personaName,
    personaRole,
    personaIdForLink,
    severity,
    type,
    recommendation,
    state,
    stateReason,
    status,
    triageNoteFromData,
    observationCount,
    observations,
    evidence,
    citations,
    fixStatus,
    fixAttempts,
    verificationResults,
    hasLinkedRepo,
    canonicalParent,
    firstSeenFormatted,
    priorityData,
    openRequest,
  };
}
